import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readPref, writePref, type PrefStorage } from '../shared/clientPrefs';
import { setStatusBar } from '../native/bridge';

// Mode-aware Theme-Logik (SPEC.md §2):
//   - Zwei Merker: ff_theme_light + ff_theme_dark.
//   - Systemmodus via matchMedia('(prefers-color-scheme: dark)') entscheidet,
//     welcher Merker AKTIV ist.
//   - Persistenz: beste legacy-Migration aus ff_theme -> richtiger Merker.
//   - Themes werden SOFORT angewendet UND in den Merker ihres (Theme-)Modus
//     geschrieben + legacy ff_theme bleibt synchron.
//   - Persistenz-Schicht = readPref/writePref (Cookie + localStorage-Write-Through):
//     iOS persistiert capacitor://-Cookies nicht ueber Kaltstarts, localStorage schon;
//     der Cookie bleibt geteilter Kanal mit den Legacy-HTML-Seiten (clientPrefs.ts).

export interface ThemePrefs {
  theme: string;
  font: string;
}

export type ThemeMode = 'light' | 'dark';

export interface ThemeContextValue extends ThemePrefs {
  setTheme: (theme: string) => void;
  setFont: (font: string) => void;
  mode: ThemeMode;
}

const DEFAULT_FONT = 'default';
export const DEFAULT_THEME_LIGHT = 'light';
export const DEFAULT_THEME_DARK = 'blue';

// Mode-Zuordnung (SPEC.md §1) — auch fuer Settings/UI als Single-Source-of-Truth.
// M6 Kosmetikpaket: starlight (Ultra exklusiv) und founders (Founders exklusiv)
// sind hinzugekommen. Mode ist hier NUR visuell (helle vs. dunkle Statusbar-
// Icons), die Berechtigung wird getrennt ueber die Entitlements gehalten.
export const THEME_MODES: Record<string, ThemeMode> = {
  light: 'light',
  sage: 'light',
  coral: 'light',
  rose: 'light',
  founders: 'light',
  blue: 'dark',
  fantasy: 'dark',
  dark: 'dark',
  gray: 'dark',
  starlight: 'dark',
};

// bg-primary-Hex pro Theme (fuer StatusBar + <meta theme-color>).
// SPEC.md §1 legt diese explizit fest.
export const THEME_BG_PRIMARY: Record<string, string> = {
  light: '#eef3f7',
  sage: '#eaf4ec',
  coral: '#fff9e5',
  rose: '#fcefef',
  founders: '#f3e6ce',
  blue: '#101e42',
  fantasy: '#392d4d',
  dark: '#12221a',
  gray: '#2c2f3a',
  starlight: '#0d1430',
};

export function getThemeMode(theme: string): ThemeMode | null {
  return THEME_MODES[theme] ?? null;
}

export function getThemeBgPrimary(theme: string): string | null {
  return THEME_BG_PRIMARY[theme] ?? null;
}

function systemModeKey(prefersDark: boolean): ThemeMode {
  return prefersDark ? 'dark' : 'light';
}

function prefersDarkMode(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches === true;
  } catch {
    return false;
  }
}

export function readThemePrefs(cookieSource = document.cookie, storage?: PrefStorage | null): ThemePrefs {
  // Default-Fallback (Modus-Defaults); spaeter durch Legacy-Migration ggf. ueberschrieben.
  // readPref liest Cookie-first mit localStorage-Fallback und normalisiert leere
  // Werte auf null, damit leere Merker nicht versehentlich ein leeres Theme aktivieren.
  let lightTheme = readPref('ff_theme_light', cookieSource, storage) ?? DEFAULT_THEME_LIGHT;
  let darkTheme = readPref('ff_theme_dark', cookieSource, storage) ?? DEFAULT_THEME_DARK;

  // Legacy-Migration: nur wenn KEINER der beiden Merker (in KEINEM der beiden
  // Speicher) gesetzt ist, das alte ff_theme in den Merker SEINES Modus
  // uebernehmen (einmaliger Migrations-Pfad). "Gesetzt" heisst: nicht-leerer
  // WERT (identische Semantik wie das Inline-No-FOUC-Script in index.html —
  // ein leerer Wert zaehlt als nicht gesetzt).
  const legacy = readPref('ff_theme', cookieSource, storage);
  const hasLightMarker = readPref('ff_theme_light', cookieSource, storage) !== null;
  const hasDarkMarker = readPref('ff_theme_dark', cookieSource, storage) !== null;
  if (!hasLightMarker && !hasDarkMarker && legacy) {
    const legacyMode = getThemeMode(legacy);
    if (legacyMode === 'light') {
      lightTheme = legacy;
    } else if (legacyMode === 'dark') {
      darkTheme = legacy;
    }
  }

  // Systemmodus entscheidet, welcher Merker das AKTIVE Theme ist.
  const prefersDark = prefersDarkMode();
  // Eine ausdrueckliche Wahl darf beim Kaltstart nicht durch den Standard
  // des anderen Systemmodus ersetzt werden (iOS-fest ueber localStorage) —
  // aber NUR im Systemmodus, in dem sie getroffen wurde. Wechselt das Geraet
  // zwischen Wahl und Kaltstart in den anderen Modus (Tag/Nacht), gilt wieder
  // der Merker dieses Modus (mode-aware, SPEC.md §2). Ohne gespeicherten Modus
  // (aeltere Wahl) greifen ebenfalls die Merker, die ihre Wahl bereits tragen.
  const selected = readPref('ff_theme_selected', cookieSource, storage);
  const selectedMode = readPref('ff_theme_selected_mode', cookieSource, storage);
  const keepSelected = selected !== null
    && getThemeMode(selected) !== null
    && selectedMode === systemModeKey(prefersDark);
  const activeTheme = keepSelected && selected
    ? selected
    : prefersDark ? darkTheme : lightTheme;

  return {
    theme: activeTheme,
    font: readPref('ff_font', cookieSource, storage) ?? DEFAULT_FONT,
  };
}

function writeMarkerCookie(theme: string, storage?: PrefStorage | null): void {
  const mode = getThemeMode(theme);
  if (mode === 'light') {
    writePref('ff_theme_light', theme, storage);
  } else if (mode === 'dark') {
    writePref('ff_theme_dark', theme, storage);
  }
}

// Einmalige Legacy-Migration PERSISTIEREN: existiert nur das alte ff_theme
// (kein Merker in KEINEM der beiden Speicher), wird es als Merker seines Modus
// festgeschrieben. Ohne das geht die Alt-Wahl beim ersten Systemmodus-Hin-und-Her
// verloren: der Modus-Wechsel spiegelt ff_theme neu und der Merker-Fallback greift
// danach auf den Modus-Default statt auf die Alt-Wahl.
export function migrateLegacyMarkerCookie(cookieSource = document.cookie, storage?: PrefStorage | null): void {
  if (readPref('ff_theme_light', cookieSource, storage) !== null) return;
  if (readPref('ff_theme_dark', cookieSource, storage) !== null) return;
  const legacy = readPref('ff_theme', cookieSource, storage);
  if (!legacy) return;
  writeMarkerCookie(legacy, storage);
}

function writeLegacyTheme(theme: string): void {
  writePref('ff_theme', theme);
}

export function applyThemePrefs(prefs: ThemePrefs): void {
  if (typeof document === 'undefined') return;
  const themeKey = prefs.theme || DEFAULT_THEME_LIGHT;
  document.documentElement.dataset.theme = themeKey;
  document.documentElement.dataset.font = prefs.font ? prefs.font : DEFAULT_FONT;

  const bg = getThemeBgPrimary(themeKey);
  if (bg) {
    // <meta name="theme-color"> defensiv anlegen, falls die Shell es nicht schon
    // mitgebracht hat (z.B. in Test-Rendern ohne den vollen index.html-DOM).
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', bg);
    // Den vom No-FOUC-Inline-Script gesetzten html-Background nachziehen —
    // sonst bliebe bei spaeterem Theme-Wechsel die Boot-Farbe in Overscroll-
    // Bereichen sichtbar.
    document.documentElement.style.background = bg;
  }

  // Native StatusBar: Light-Theme -> 'light' (dunkle Icons), Dark-Theme -> 'dark' (helle Icons).
  // bridge.ts mappt 'light' -> Capacitor Style.Light, 'dark' -> Style.Dark.
  // Hintergrund = bg-primary-Hex des aktiven Themes.
  const mode = getThemeMode(themeKey);
  if (mode) {
    const style = mode === 'light' ? 'light' : 'dark';
    void setStatusBar(style, bg ?? undefined).catch(() => undefined);
  }
}

export function persistTheme(theme: string): void {
  // setTheme-Pfad: Marker des THEME-Modus schreiben UND legacy ff_theme spiegeln.
  const normalized = theme || DEFAULT_THEME_LIGHT;
  writeMarkerCookie(normalized);
  // Ausdrueckliche Wahl samt dem Systemmodus, in dem sie getroffen wurde
  // (siehe readThemePrefs).
  writePref('ff_theme_selected', normalized);
  writePref('ff_theme_selected_mode', systemModeKey(prefersDarkMode()));
  writeLegacyTheme(normalized);
}

// Wird vom matchMedia-change-Listener aufgerufen: aktives Theme = Merker des
// neuen Modus. Marker-Cookies unveraendert (bleiben bis zur naechsten Auswahl),
// nur die legacy-Spiegelung wird nachgezogen.
function syncActiveThemeCookie(theme: string): void {
  writeLegacyTheme(theme);
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<ThemePrefs>(() => {
    // WICHTIG: Migration VOR dem Lesen persistieren (readThemePrefs migriert
    // nur in-memory) — sonst verliert ein Alt-User seine ff_theme-Wahl beim
    // ersten Systemmodus-Wechsel dauerhaft.
    migrateLegacyMarkerCookie();
    const initial = readThemePrefs();
    applyThemePrefs(initial);
    syncActiveThemeCookie(initial.theme);
    return initial;
  });

  const setTheme = useCallback((nextTheme: string) => {
    const theme = nextTheme || DEFAULT_THEME_LIGHT;
    persistTheme(theme);
    setPrefs((current) => {
      const next = { ...current, theme };
      applyThemePrefs(next);
      return next;
    });
  }, []);

  const setFont = useCallback((nextFont: string) => {
    const font = nextFont || DEFAULT_FONT;
    writePref('ff_font', font);
    setPrefs((current) => {
      const next = { ...current, font };
      applyThemePrefs(next);
      return next;
    });
  }, []);

  // matchMedia-Wechsel: Systemmodus aendert sich -> Merker des NEUEN Modus
  // wird aktiv; legacy ff_theme wird nachgezogen; KEINE Marker-Mutation.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      setPrefs((current) => {
        const prefersDark = mql.matches === true;
        // readPref liest Cookie-first mit localStorage-Fallback und behandelt
        // leere Marker-Werte als "nicht gesetzt" (gleiche Semantik wie
        // readThemePrefs/Inline-Script) — sonst wuerde theme:'' angewendet und
        // ff_theme mit Leerstring verschmutzt.
        const marker = prefersDark
          ? (readPref('ff_theme_dark') ?? DEFAULT_THEME_DARK)
          : (readPref('ff_theme_light') ?? DEFAULT_THEME_LIGHT);
        if (marker === current.theme) return current;
        const next = { ...current, theme: marker };
        syncActiveThemeCookie(marker);
        applyThemePrefs(next);
        return next;
      });
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const mode: ThemeMode = getThemeMode(prefs.theme) ?? 'light';

  const value = useMemo<ThemeContextValue>(
    () => ({ ...prefs, mode, setTheme, setFont }),
    [prefs, mode, setFont, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within ThemeProvider');
  return value;
}
